<template>
    <div>
        <b-card-actions noActions>
            <div class="d-flex justify-content-between align-items-right">
                <h4 class="mb-0">House Info</h4>
                <b-button
                    v-if="!isNoHousePin && !housePinId"
                    class="ms-auto"
                    variant="outline-primary"
                    @click="onNewHousePin"
                >
                    {{ `Add Manual Address` }}
                </b-button>

                <b-button
                    v-if="isNoHousePin && !housePinId"
                    class="ms-auto"
                    variant="outline-primary"
                    @click="onSearchHousePin"
                >
                    {{ `Search Address` }}
                </b-button>
            </div>

            <b-row>
                <b-col md="12">
                    <h5 class="mb-0 mt-1">
                        <feather-icon icon="MapPinIcon" class="mr-50" />Location
                    </h5>
                    <hr class="mt-50" />
                </b-col>
            </b-row>

            <b-row v-if="!isNoHousePin">
                <b-col md="12">
                    <b-form-group label="House Pin">
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="house pin"
                        >
                            <vue-autosuggest
                                v-model="pinSelected"
                                :suggestions="[{ data: hasTyped && filteredHousePins.length ? filteredHousePins : [] }]"
                                :input-props="{
                                    id: 'house-pin',
                                    placeholder: 'Search house pin',
                                    class: 'form-control',
                                    disabled: housePinId ? true : false
                                }"
                                :get-suggestion-value="item => item.item.name"
                                @selected="onHousePinSelect"
                                @input="onHousePinSearchInput"
                            >
                                <template slot-scope="{ suggestion }">
                                    <div class="autosuggest__results-item">
                                        {{ suggestion.item.name }}
                                    </div>
                                </template>
                            </vue-autosuggest>
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row v-if="isNoHousePin">
                <b-col md="12">
                    <b-tabs>
                        <b-tab
                            v-for="(locale, key) in languageData"
                            :key="locale.code"
                            :title="locale.name"
                        >
                            <b-form-group :label="`House Pin Name (${locale.name})`">
                                <validation-provider
                                    #default="{ errors }"
                                    rules="required"
                                    :name="`house pin name (${locale.name})`"
                                >
                                    <b-form-input
                                        v-model="form.newHousePinName[locale.code]"
                                        placeholder="Enter house pin name"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>

                            <b-form-group :label="`Address (${locale.name})`">
                                <b-form-input
                                    v-model="form.newHousePinAddress[locale.code]"
                                    placeholder="Enter Address"
                                />
                            </b-form-group>
                        </b-tab>
                    </b-tabs>
                </b-col>
            </b-row>

            <b-row v-if="isNoHousePin">
                <b-col md="6">
                    <b-form-group label="Province">
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="province id"
                        >
                            <v-select
                                v-model="form.province_id"
                                :options="provinceOptions"
                                :reduce="a => a.id"
                                label="name"
                                placeholder="Select a province"
                                @input="onProvinceChange"
                                class="custom-v-select"
                            />
                            <!-- :disabled="form.house_pin && !form.house_pin.isNew" -->
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="District/Amphure">
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="district id"
                        >
                            <v-select
                                v-model="form.amphure_id"
                                :options="amphureOptions"
                                :reduce="a => a.id"
                                label="name"
                                placeholder="Select an amphure"
                                @input="onAmphureChange"
                                class="custom-v-select"
                            />
                            <!-- :disabled="form.house_pin && !form.house_pin.isNew" -->
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Sub District/Tambon">
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="tambon id"
                        >
                            <v-select
                                v-model="form.tambon_id"
                                :options="tambonOptions"
                                :reduce="t => t.id"
                                label="name"
                                placeholder="Select a tambon"
                                @input="onTambonChange"
                                class="custom-v-select"
                            />
                            <!-- :disabled="form.house_pin && !form.house_pin.isNew" -->
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>

               <b-col md="6">
                    <b-form-group label="Zip Code">
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="zip code"
                        >
                            <b-form-input v-model="form.zip_code" readonly />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>

                 <b-col>
                    <b-row>
                        <location-selector
                            label="BTS"
                            :options="btsOptions"
                            :data.sync="form.btsData"
                        />

                        <location-selector
                            label="MRT"
                            :options="mrtOptions"
                            :data.sync="form.mrtData"
                        />
                    </b-row>

                    <b-row>
                        <location-selector
                            label="Airport Link"
                            :options="airportLinkOptions"
                            :data.sync="form.airportLinkData"
                        />

                        <b-col md="6">
                            <b-form-group>
                                <label>Zone</label>
                                <v-select
                                    v-model="form.zoneId"
                                    :options="zoneOptions"
                                    label="name"
                                    :reduce="zone => zone.id"
                                    multiple
                                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12">
                    <h5 class="mb-0 mt-1">
                        <feather-icon icon="HomeIcon" class="mr-50" />Property Details
                    </h5>
                    <hr class="mt-50" />
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12">
                    <b-form-group label="House Type">
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="house type"
                        >
                            <v-select
                                v-model="form.house_type"
                                :options="houseTypeOptions.map((a) => ({
                                    title: a.title[currentLocale],
                                    id: a.id
                                }))"
                                :reduce="t => t.id"
                                label="title"
                                placeholder="Select a house type"
                                class="custom-v-select"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="3">
                    <b-form-group label="House No.">
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="house no."
                        >
                            <b-form-input v-model="form.house_no" />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>

                <b-col md="3">
                    <b-form-group label="Unit">
                        <b-form-input v-model="form.unit" />
                    </b-form-group>
                </b-col>

                <b-col md="3">
                    <b-form-group label="Floor">
                        <b-form-input v-model="form.floor" />
                    </b-form-group>
                </b-col>

                <b-col md="3">
                    <b-form-group label="Plan Type">
                        <b-form-input v-model="form.plan_type" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12">
                    <h5 class="mb-0 mt-1">
                        <feather-icon icon="SlidersIcon" class="mr-50" />Specifications
                    </h5>
                    <hr class="mt-50" />
                </b-col>
            </b-row>

            <b-row>
                <b-col md="3">
                    <b-form-group label="Bedroom">
                        <b-form-spinbutton
                            v-model="form.bedroom"
                            min="0"
                        />
                    </b-form-group>
                </b-col>

                <b-col md="3">
                    <b-form-group label="Bathroom">
                        <b-form-spinbutton
                            v-model="form.bathroom"
                            min="0"
                        />
                    </b-form-group>
                </b-col>

                <b-col md="3">
                    <b-form-group label="Parking">
                        <b-form-spinbutton
                            v-model="form.parking"
                            min="0"
                        />
                    </b-form-group>
                </b-col>

                <b-col md="3" class="d-flex align-items-center justify-content-center">
                    <b-form-group class="mb-0">
                        <b-form-checkbox
                            id="new-house"
                            v-model="form.is_new_house"
                            name="new-house"
                            value="true"
                        >
                            New House
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Usable Area (sqm.)">
                        <validation-provider
                            #default="{ errors }"
                            name="usable-area"
                        >
                            <b-form-input v-model="form.useable_area" />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Land Area (sqw.)">
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="land-area"
                        >
                            <b-form-input v-model="form.land_area" />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="6">
                    <b-form-group label="Direction">
                        <v-select
                            v-model="form.direction_id"
                            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                            label="title"
                            :options="directionOptions"
                        />
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Furniture">
                        <v-select
                            v-model="form.furniture_id"
                            :reduce="t => t.id"
                            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                            label="name"
                            :options="furnitureOptions"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12">
                    <h5 class="mb-0 mt-1">
                        <feather-icon icon="TagIcon" class="mr-50" />Listing Status
                    </h5>
                    <hr class="mt-50" />
                </b-col>
            </b-row>

            <b-row>
                <b-col md="6">
                    <b-form-group label="Action">
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="action"
                        >
                            <v-select
                                v-model="form.action_id"
                                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                label="name"
                                :reduce="(option) => option.id"
                                :options="actionOptions"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Status">
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="status"
                        >
                            <v-select
                                v-model="form.status_id"
                                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                label="name"
                                :reduce="(option) => option.id"
                                :options="statusOptions"
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="6">
                    <b-form-group label="Expire date">
                            <b-form-datepicker
                            id="expire_date"
                            v-model="form.expire_date"
                            reset-button
                            close-button
                            class="mb-1"
                            
                        />
                        <!-- @input="emitToParent" -->
                    </b-form-group>
                </b-col>

                <b-col md="6">
                    <b-form-group label="Updated Date (expire date, price, status)">
                        <b-form-datepicker
                            id="updated_date"
                            v-model="form.updated_date"
                            reset-button
                            close-button
                            class="mb-1"
                        />
                    </b-form-group>
                </b-col>

                <b-col md="6" class="d-flex align-items-center">
                    <b-form-group class="mb-0">
                        <b-form-checkbox
                            checked="true"
                            class="custom-control-primary"
                            name="is_active"
                            v-model="form.is_active"
                            switch
                        >
                            Active
                        </b-form-checkbox>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12">
                    <h5 class="mb-0 mt-1">
                        <feather-icon icon="FileTextIcon" class="mr-50" />Marketing and Description
                    </h5>
                    <hr class="mt-50" />
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12">
                    <b-form-group label="Youtube URL">
                        <b-form-input
                            v-model="form.youtube_url"
                            :placeholder="`Youtube URL`"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12">
                    <b-form-group label="Property Comment">
                        <b-form-textarea
                            id="comment"
                            v-model="form.comment"
                            placeholder="comment"
                        />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-tabs content-class="mt-1" v-if="languageData.length > 0">
                <b-tab v-for="(lang, key) in languageData" :key="'tab-' + key" :title="lang.name">
                    <b-row>
                        <b-col md="6">
                            <b-form-group :label="`Meta Title (${lang.code})`">
                                <validation-provider
                                    #default="{ errors }"
                                    name="meta title"
                                >
                                    <b-form-input
                                        v-model="form.meta_title[lang.code]"
                                        :placeholder="`Meta Title (${lang.name})`"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>

                        <b-col md="6">
                            <b-form-group :label="`Meta Description (${lang.code})`">
                                <validation-provider
                                    #default="{ errors }"
                                    name="meta description"
                                >
                                    <b-form-input
                                        v-model="form.meta_description[lang.code]"
                                        :placeholder="`Meta Description (${lang.name})`"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-tab>
            </b-tabs>

            <b-row v-if="housePinId && userName">
                <b-col md="12">
                    <hr class="mt-1 mb-50" />
                    <small class="text-muted">
                        <feather-icon icon="UserIcon" class="mr-25" />Created by {{ userName }}
                    </small>
                </b-col>
            </b-row>
        </b-card-actions>
    </div>
</template>

<script>
import {
    BRow, BCol, BFormGroup, BButton, BFormInput, BTabs, BTab, BFormSpinbutton, BFormDatepicker,
    BFormTextarea, BFormCheckbox,
} from 'bootstrap-vue'
import { VueAutosuggest } from 'vue-autosuggest'
import { ValidationProvider } from 'vee-validate'
import BCardActions from '../../../@core/components/b-card-actions/BCardActions.vue'
import debounce from "lodash/debounce"
import vSelect from 'vue-select'
import router from '@/router'
import axios from '@axios'
import LocationSelector from '@/views/components/locationSelector/LocationSelector.vue'

export default {
    props: {
        form: { type: Object, required: true }
    },
    components: {
        BCardActions, BRow, BCol, BFormGroup, BButton, BTabs, BTab,
        vSelect, BFormInput, BFormSpinbutton, BFormDatepicker, BFormTextarea, BFormCheckbox,
        ValidationProvider, VueAutosuggest, LocationSelector,
    },
    data() {
        const userData = JSON.parse(localStorage.getItem('userData'));
        
        return {
            directionOptions: [
                { id: 1, title: 'North' }, 
                { id: 2, title: 'East' }, 
                { id: 3, title: 'South' }, 
                { id: 4, title: 'West' }, 
                { id:5, title:'North East'},
                { id:6, title:'North West'},
                { id:7, title:'South East'},
                { id:8, title:'South West'},
            ],
            statusOptions:[
                { id: 1, name: 'Available now' }, 
                { id: 2, name: 'Rented' }, 
                { id: 3, name: 'Sale with tenant' },
                { id: 5, name: 'Own Stay' },
                { id: 6, name: 'Not Sure' },
                { id: 7, name: 'Cannot contact' },
                { id: 8, name: 'Follow up' },
                { id: 4, name: 'Sold' }
            ],
            housePinId: router.currentRoute.params.id || null,
            newHousePinMode: false,
            pinSelected: null,
            filteredHousePins: [],
            hasTyped: false,
            houseTypeOptions: [],
            languageData: [],
            isNoHousePin: true,
            provinceOptions: [],
            amphureOptions: [],
            tambonOptions: [],
            btsData: [],
            mrtData: [],
            airportLinkData: [],
            zoneData: [],
            furnitureOptions: [],
            actionOptions: [],
            userName: userData.firstname,
        }
    },
    created() {
        this.fetchAndSet('transit/v1/lists', { type_code: 'bts', is_active: 1 }, 'btsData'),
        this.fetchAndSet('transit/v1/lists', { type_code: 'mrt', is_active: 1 }, 'mrtData'),
        this.fetchAndSet('transit/v1/lists', { type_code: 'airport', is_active: 1 }, 'airportLinkData'),
        this.fetchAndSet('zone/v1/lists', { is_active: 1 }, 'zoneData'),
        setTimeout(() => this.initData(), 300)
        // this.fetchLanguageData()
        console.log('22')
        // this.fetchHousePinDetail()
        this.debouncedHousePinSearch = debounce(this.onHousePinSearch.bind(this), 300)
        console.log('33')
    },
    methods: {
        async fetchAndSet(url, payload, target) {
            try {
                const { data } = await axios.post(url, payload)
                this[target] = data?.data || []
            } catch (error) {
                console.error(`Error fetching ${target}:`, error)
                this[target] = []
            }
        },
        async initData() {
            console.log('11', this.form.house_pin_id)
            await this.fetchProvinces()
            await this.fetchLanguageData()
            await this.fetchHouseTypeData()
            await this.fetchFurnitureTypeData()
            await this.fetchActionData()
            if(this.form.house_pin_id) await this.fetchHousePinDetail()
            if(this.form.province_id) await this.onProvinceChange(this.form.province_id)
            if(this.form.amphure_id) await this.onAmphureChange(this.form.amphure_id)
            if(this.form.tambon_id) await this.onTambonChange(this.form.tambon_id)
        },
        async fetchLanguageData() {
            try {
                const { data } = await axios.post("languages/v1/getall", { is_active: 1 })

                this.languageData = data.data
            } catch (error) {
                console.error('Error fetching language:', error)
            }
        },
        async fetchHouseTypeData() {
            try {
                const { data } = await axios.post("v1/house-types", {
                    is_active: 1
                })
                
                this.houseTypeOptions = data.data
            } catch (error) {
                console.error('Error fetching house types:', error)
            }
        },
        onSearchHousePin() {
            this.isNoHousePin = false
            this.pinSelected = ""
            this.filteredHousePins = []
            this.newHousePinName = {}
            this.newHousePinAddress = {}
            this.form.house_pin = {}
            this.form.house_pin_id = null
        },
        async onNewHousePin() {
            this.isNoHousePin = true
            this.pinSelected = ""
            this.filteredHousePins = []
            this.newHousePinName = {}
            this.newHousePinAddress = {}
            this.form.house_pin = {}
            this.form.house_pin_id = null
        },
        onHousePinSelect(suggestion) {
            if (!suggestion?.item) return;

            this.isNoHousePin = false
            this.form.house_pin = suggestion.item;
            this.form.house_pin_id = suggestion.item.id;
            this.pinSelected = suggestion.item.name;
        },
        onHousePinSearchInput(value) {
            console.log('value', value)
            this.searchHousePin = value
            this.hasTyped = true
            this.debouncedHousePinSearch(value)
        },
        async onHousePinSearch(search) {
            console.log('search', search)
            this.hasTyped = true
            this.filteredHousePins = []

            if (!search || search.length < 2) return

            try {
                const { data } = await axios.post(`/v1/house-pin/search`, {
                    keyword: search
                })

                this.filteredHousePins = data?.data.map(hp => ({
                    id: hp.id,
                    name: hp.name[this.currentLocale] || hp.name.en,
                    province_id: hp.province_id,
                    amphure_id: hp.amphure_id,
                    district_id: hp.district_id,
                    zip_code: hp.zip_code
                }))
            } catch (error) {
                console.error("Error fetching house pins:", error)
                this.filteredHousePins = []
            }
        },
        async fetchHousePinDetail() {
            try {
                const { data } = await axios.get(`v1/house-pin/${this.form.house_pin_id}`)
                console.log('data', data)
                const itemData = {
                    id: data.data.id,
                    name: data.data.name[this.currentLocale] || data.data.name.en,
                    province_id: data.data.province_id,
                    amphure_id: data.data.amphure_id,
                    district_id: data.data.district_id,
                    zip_code: data.data.zip_code
                };

                this.selectedHousePin = itemData;
                this.onHousePinSelect({ item: itemData });
            } catch (error) {
                console.error('Error fetching existing house pin:', error)
            }
        },
        async fetchProvinces() {
            try {
                const { data } = await axios.get("address/v1/provinces")
                this.provinceOptions = data?.data.map(p => ({
                    id: p.id,
                    name: this.currentLocale === "th" ? p.name_th : p.name_en
                }))
            } catch (error) {
                console.error('Error fetching provinces:', error)
                this.provinceOptions = []
            }
        },
        async onProvinceChange(provinceId) {
            // this.form.amphure_id = null
            // this.form.tambon_id = null
            // this.form.zip_code = ''

            console.log('find province', provinceId)
            if (!provinceId) return
            try {
                const { data } = await axios.get(`amphures/${provinceId}`)
                this.amphureOptions = data?.data.map(a => ({
                    id: a.id,
                    name: this.currentLocale === "th" ? a.name_th : a.name_en
                }))
            } catch (error) {
                console.error('Error fetching amphures:', error)
                // this.amphureOptions = []
            }
        },
        async onAmphureChange(amphureId) {
            // this.tambonOptions = [];
            // this.form.zip_code = ''

            console.log('am2', amphureId)
            if (!amphureId) return
            try {
                const { data } = await axios.get(`tambons/${amphureId}`)
                console.log('check get am', data)
                this.tambonOptions = data?.data.map(t => ({
                    id: t.id,
                    name: this.currentLocale === "th" ? t.name_th : t.name_en
                }))
            } catch (error) {
                console.error('Error fetching tambons:', error)
                // this.tambonOptions = []
            }
        },
        async onTambonChange(tambonId) {
            if (!tambonId) return
            try {
                const { data } = await axios.get(`zipcode/${tambonId}`)
                this.form.zip_code = data?.data?.[0]?.zip_code || null
            } catch (error) {
                console.error('Error fetching zip code:', error)
                // this.form.zip_code = null
            }
        },
        mapLocalizedOptions(data, key = 'name') {
            return data.map(g => ({
                id: g.id,
                name: g[key]?.[this.currentLocale] || g[key]
            }))
        },
        async fetchFurnitureTypeData() {
            try {
                const { data } = await axios.get("furnituretype/v1/list")
                
                this.furnitureOptions = data.data
            } catch (error) {
                console.error('Error fetching furniture:', error)
            }
        },
        async fetchActionData() {
            try {
                const { data } = await axios.get("property-action/v1/list")

                this.actionOptions = data.data
            } catch (error) {
                console.error('Error fetching action:', error)
            }
        },
    },
    computed: {
        currentLocale() {
            return this.$i18n.locale
        },
        btsOptions() {
            return this.mapLocalizedOptions(this.btsData)
        },
        mrtOptions() {
            return this.mapLocalizedOptions(this.mrtData)
        },
        airportLinkOptions() {
            return this.mapLocalizedOptions(this.airportLinkData)
        },
        zoneOptions() {
            return this.mapLocalizedOptions(this.zoneData)
        },
    },
}
</script>